import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useStepUpStore } from '@/stores/stepUp'
import { authorizationHeaders } from '@/services/sessionToken'
import { readPublicBoot } from '@/services/publicBoot'

// Generate a UUID v4 for session identification
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

// Get or create a persistent session ID for API requests
// This ensures token-based pagination works correctly since Rails
// API uses null_session (session_id is nil without this header)
const SESSION_ID_KEY = 'tower_hunt_session_id'
function getOrCreateSessionId() {
  let sessionId = localStorage.getItem(SESSION_ID_KEY)
  if (!sessionId) {
    sessionId = generateUUID()
    localStorage.setItem(SESSION_ID_KEY, sessionId)
  }
  return sessionId
}

// There is no API-version selection here. v2 IS the application: every request
// this store builds targets /api/v2 unconditionally, and the store makes no
// request of its own before mount.
export const useApiVersioning = defineStore('apiVersioning', () => {
  // Persistent session ID for bitmap token-based operations
  const sessionId = getOrCreateSessionId()

  // Web push (web-push-notifications D9/D10). The runtime kill switch and the
  // non-secret VAPID application-server key ride the boot blob the shell
  // renders (launch-marketing-pages M13), so usePushSubscription reads them
  // here rather than from build-time import.meta.env — flipping
  // WEB_PUSH_ENABLED takes effect on the next page load with no rebuild and no
  // request before mount.
  const webPushEnabled = computed(() => readPublicBoot().web_push_enabled)
  const vapidPublicKey = computed(() => readPublicBoot().vapid_public_key)

  // API URL builders
  const buildApiUrl = (endpoint) => `/api/v2${endpoint}`

  // Request configuration
  const getRequestConfig = (options = {}) => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        // Include session ID for token-based pagination/billing
        // Rails API uses null_session so request.session.id is nil without this
        'X-Session-ID': sessionId,
        'X-API-Version': 'v2'
      }
    }

    // The session bearer, from its one owner (th-vfii1) — read per request,
    // so a renewal, sign-out or user switch applies to the next call.
    Object.assign(config.headers, authorizationHeaders())

    // Include cached step-up proof if one is held. Called lazily (never at
    // module top level) to avoid store-init-order cycles. System headers take
    // precedence in baseFetch's merge, so a cached proof rides every v2
    // request automatically; no caller passes the header.
    const stepUpToken = useStepUpStore().token
    if (stepUpToken) {
      config.headers['X-Step-Up-Token'] = stepUpToken
    }

    return config
  }

  return {
    sessionId,
    webPushEnabled,
    vapidPublicKey,
    buildApiUrl,
    getRequestConfig
  }
})